/**
 * One-time migration when items became company-specific:
 * - companyIds: every company that has invoices / purchase bills containing this item
 *   (none yet → all companies so existing catalog stays available).
 * Runs once after login (flag settings/migrations.itemCompanies); items that already have
 * companyIds are left alone, so running it twice changes nothing.
 */
import { doc, getDoc, setDoc, writeBatch, serverTimestamp } from "firebase/firestore";
import { db } from "../../../firebase/firestore.js";
import { auth } from "../../../firebase/auth.js";
import { writeAudit } from "../../../firebase/auditLogger.js";
import { itemStore } from "../hooks/useItems.js";
import { invoiceStore } from "../../invoices/hooks/useInvoices.js";
import { purchaseStore } from "../../purchases/hooks/usePurchases.js";
import { UNASSIGNED_COMPANY, getRecordCompanyKey } from "../../../shared/utils/companyScope.js";

const FLAG_DOC = () => doc(db, "settings", "migrations");
const lower = (v) => String(v || "").trim().toLowerCase();

/**
 * Works out companyIds for items that don't have them yet. Pure — used by the app and tests.
 */
export function planItemCompanies({ items, invoices, purchases, companies, defaultCompanyId }) {
  const companyKey = (bill) => {
    const key = getRecordCompanyKey(bill, companies);
    return key === UNASSIGNED_COMPANY ? defaultCompanyId : key;
  };
  const allIds = companies.map((c) => c.id);
  const updates = [];

  items.forEach((item) => {
    if (Array.isArray(item.companyIds) && item.companyIds.length) return;

    const name = lower(item.itemName || item.name);
    const hasItem = (b) => {
      const lineItems = Array.isArray(b.items) ? b.items : [];
      return lineItems.some((li) => {
        if (li.itemId && li.itemId === item.id) return true;
        if (li.id && li.id === item.id) return true;
        return name && lower(li.itemName || li.name) === name;
      });
    };

    const sales = invoices.filter(hasItem);
    const buys = purchases.filter(hasItem);
    const used = [...new Set([...sales, ...buys].map(companyKey).filter(Boolean))];
    const companyIds = used.length ? allIds.filter((id) => used.includes(id)) : allIds;

    updates.push({ id: item.id, companyIds });
  });

  return updates;
}

let running = null;

/**
 * Called after login once companies have loaded. Safe to call many times.
 */
export function ensureItemCompanies({ companies, defaultCompany }) {
  if (running || !companies?.length) return running;
  running = (async () => {
    const flag = await getDoc(FLAG_DOC());
    if (flag.exists() && flag.data()?.itemCompanies) return { skipped: true };

    const [items, invoices, purchases] = await Promise.all([
      itemStore.load(),
      invoiceStore.load(),
      purchaseStore.load()
    ]);

    const updates = planItemCompanies({
      items,
      invoices,
      purchases,
      companies,
      defaultCompanyId: defaultCompany?.id || companies[0].id
    });

    for (let i = 0; i < updates.length; i += 400) {
      const batch = writeBatch(db);
      updates.slice(i, i + 400).forEach((u) => {
        batch.update(doc(db, "items", u.id), {
          companyIds: u.companyIds,
          updatedAt: serverTimestamp()
        });
      });
      await batch.commit();
    }

    const by = auth.currentUser?.email || auth.currentUser?.uid || "admin";
    await setDoc(
      FLAG_DOC(),
      { itemCompanies: { at: new Date().toISOString(), by, count: updates.length } },
      { merge: true }
    );
    writeAudit("ITEMS_ASSIGNED_TO_COMPANIES", { count: updates.length });
    if (updates.length) await itemStore.refresh();
    return { updated: updates.length };
  })().catch((err) => {
    console.error("Assigning items to companies failed (will retry next time):", err);
    running = null;
    return { error: err.message };
  });

  return running;
}
